<template>
  <div :id="componentId">
    <div v-if="title" class="container mx-auto mb-12 flex max-w-screen-xl items-center justify-between px-0">
      <div class="flex flex-col">
        <h2 v-if="title" class="text-3xl font-medium">
          {{ title }}
        </h2>
        <p v-if="subtitle" class="mt-4">
          {{ subtitle }}
        </p>
      </div>
      <!-- Navigation buttons-->
      <div v-if="navigation" class="inno-carousel__navigation">
        <div class="inno-carousel__btn inno-carousel__btn--prev cursor-pointer">
          <VcIcon name="chevron-left" :size="14" />
        </div>

        <div class="inno-carousel__btn inno-carousel__btn--next ml-2 cursor-pointer">
          <VcIcon name="chevron-right" :size="14" />
        </div>
      </div>
    </div>

    <div class="inno-carousel__wrapper">
      <Swiper
        :modules="modules"
        :navigation="navigationParams"
        :pagination="paginationParams"
        v-bind="attrs"
        class="w-full"
        v-on="listeners"
      >
        <SwiperSlide v-for="(slide, index) in slides" :key="index">
          <slot name="slide" v-bind="{ slide, index }">
            {{ slide }}
          </slot>
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import _ from "lodash";
import { Pagination, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue"; // eslint-disable-line import/no-unresolved
import { computed, getCurrentInstance } from "vue";
import type { NavigationOptions, PaginationOptions } from "swiper/types";
import type { PropType } from "vue";

const props = defineProps({
  slides: {
    type: Array as PropType<any[]>,
    default: () => [],
  },

  options: {
    type: Object as PropType<ICarouselOptions>,
    default: () => ({}),
  },

  navigation: {
    type: Boolean,
    default: false,
  },

  pagination: {
    type: Boolean,
    default: false,
  },

  title: {
    type: String,
    default: "",
  },

  subtitle: {
    type: String,
    default: "",
  },

  titleComponent: {
    type: String,
    default: "",
  },
});

const componentId = `inno-carousel_${getCurrentInstance()!.uid}`;
const modules = [Pagination, Navigation];

const listeners = computed(() => props.options.on ?? {});

const attrs = computed<Omit<ICarouselOptions, "on">>(() => {
  const options = _.clone(props.options);
  delete options.on;
  return options;
});

const navigationParams = computed<NavigationOptions | boolean>(() =>
  props.navigation
    ? {
        prevEl: `#${componentId} .inno-carousel__btn--prev`,
        nextEl: `#${componentId} .inno-carousel__btn--next`,
        lockClass: "inno-carousel__btn--lock",
        hiddenClass: "inno-carousel__btn--hidden",
        disabledClass: "inno-carousel__btn--disabled",
      }
    : false,
);

const paginationParams = computed<PaginationOptions | boolean>(() =>
  props.pagination
    ? {
        clickable: true,
      }
    : false,
);
</script>

<style lang="scss">
.inno-carousel {
  $self: &;

  --navigation-size: 36px;
  --navigation-offset: 0px;
  --pagination-offset: -5px;

  --swiper-pagination-bullet-size: 13px;
  --swiper-pagination-bullet-inactive-color: transparent;
  --swiper-pagination-bullet-inactive-opacity: 1;

  &__wrapper {
    @apply mx-auto relative overflow-visible;
  }

  &--navigation {
    #{$self}__wrapper {
    }
  }

  &__navigation {
    @apply flex justify-center items-center h-6;
  }

  &__btn {
    @apply flex justify-center items-center rounded-full bg-[var(--color-primary-500)] text-[color:var(--color-white)] w-6 h-6;

    &--prev {
      @apply left-0;
    }

    &--next {
      @apply right-0;
    }

    &--disabled {
      @apply cursor-auto pointer-events-none opacity-30;
    }

    &--lock {
      @apply hidden;
    }
  }

  .swiper-pagination-fraction,
  .swiper-pagination-custom,
  .swiper-horizontal > .swiper-pagination-bullets,
  .swiper-pagination-bullets.swiper-pagination-horizontal {
    bottom: -80px;
  }

  .swiper-pagination-bullet {
    @apply border-2 border-[var(--color-primary-500)];
  }

  .swiper {
    overflow: visible;
  }
}
</style>
